<script lang="ts">
  import type { Recommendation, RecommendationPriority, PontotelModule } from '$core/recommendations/recommendation.types';
  import Card from '../../components/Card.svelte';
  import EmptyState from '../../components/EmptyState.svelte';

  export let recommendations: Recommendation[];

  const priorityLabel: Record<RecommendationPriority, string> = {
    critical: 'Crítico',
    high: 'Alta',
    medium: 'Média',
    low: 'Baixa',
  };

  const moduleLabel: Record<PontotelModule, string> = {
    'registro-de-ponto': 'Registro de Ponto',
    'medidas-antifraude': 'Antifraude',
    'calculos-e-tratamento': 'Cálculos e Tratamento',
    'fechamento-colaborativo': 'Fechamento Colaborativo',
    'controle-de-custos': 'Controle de Custos',
    'gestao-participativa': 'Gestão Participativa',
    'reducao-de-riscos': 'Redução de Riscos',
  };

  function priorityClass(priority: RecommendationPriority): string {
    switch (priority) {
      case 'critical': return 'badge--critical';
      case 'high': return 'badge--high';
      case 'medium': return 'badge--medium';
      case 'low': return 'badge--low';
      default: return 'badge--low';
    }
  }
</script>

<section class="recommendations-panel" aria-label="Recomendações">
  {#if recommendations.length === 0}
    <EmptyState message="Nenhuma recomendação gerada para este diagnóstico." />
  {:else}
    <div class="recommendations-list">
      {#each recommendations as rec (rec.code)}
        <Card>
          <div class="rec-card">
            <div class="rec-card__header">
              <span class="badge {priorityClass(rec.priority)}" aria-label="Prioridade: {priorityLabel[rec.priority]}">
                {priorityLabel[rec.priority]}
              </span>
              {#if rec.soft}
                <span class="badge badge--soft" aria-label="Recomendação leve">leve</span>
              {/if}
            </div>

            <p class="rec-card__pain">{rec.pain}</p>
            <p class="rec-card__finding">{rec.finding}</p>
            <p class="rec-card__message">{rec.message}</p>

            <div class="rec-card__modules">
              <span class="chip chip--primary">{moduleLabel[rec.module]}</span>
              {#if rec.secondaryModules}
                {#each rec.secondaryModules as mod (mod)}
                  <span class="chip chip--secondary">{moduleLabel[mod]}</span>
                {/each}
              {/if}
            </div>

            <div class="rec-card__next-step">
              <span class="rec-card__next-step-label">Próximo passo:</span>
              <span class="rec-card__next-step-text">{rec.nextStep}</span>
            </div>
          </div>
        </Card>
      {/each}
    </div>
  {/if}
</section>

<style>
  .recommendations-panel {
    width: 100%;
  }

  .recommendations-list {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  .rec-card {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3);
  }

  .rec-card__header {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-2);
    flex-wrap: wrap;
  }

  /* Badges */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: var(--ptt-space-1) var(--ptt-space-3);
    border-radius: var(--ptt-radius-sm);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .badge--critical {
    background: var(--ptt-orange-100);
    color: var(--ptt-white);
  }

  .badge--high {
    background: var(--ptt-yellow-200);
    color: var(--ptt-black);
  }

  .badge--medium {
    background: var(--ptt-blue-100);
    color: var(--ptt-white);
  }

  .badge--low {
    background: var(--ptt-gray-100);
    color: var(--ptt-white);
  }

  .badge--soft {
    background: var(--ptt-gray-20);
    color: var(--ptt-gray-200);
  }

  /* Card content */
  .rec-card__pain {
    font-size: 1rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
    margin: 0;
  }

  .rec-card__finding {
    font-size: 0.8125rem;
    color: var(--ptt-gray-100);
    margin: 0;
  }

  .rec-card__message {
    font-size: 0.9375rem;
    color: var(--ptt-gray-200);
    margin: 0;
    line-height: 1.5;
  }

  /* Module chips */
  .rec-card__modules {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ptt-space-2);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    padding: var(--ptt-space-1) var(--ptt-space-3);
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .chip--primary {
    background: var(--ptt-purple-100);
    color: var(--ptt-white);
  }

  .chip--secondary {
    background: var(--ptt-gray-20);
    color: var(--ptt-gray-200);
  }

  /* Next step */
  .rec-card__next-step {
    display: flex;
    gap: var(--ptt-space-2);
    align-items: flex-start;
    padding: var(--ptt-space-3);
    background: var(--ptt-gray-10);
    border-radius: var(--ptt-radius-md);
    border-left: 3px solid var(--ptt-purple-100);
  }

  .rec-card__next-step-label {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--ptt-purple-100);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .rec-card__next-step-text {
    font-size: 0.8125rem;
    color: var(--ptt-gray-200);
    line-height: 1.5;
  }
</style>
